import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, CheckCircle, PlusCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { generateSignedUrl } from '@/api/user';
import { debounce } from '@/utils/helper';
import { notification } from 'antd';

interface VoiceAudioPlayerProps {
	fileName: string;
	onPlay?: () => void;
	onPause?: () => void;
	currentlyPlayingIndex: number | null;
	index: number;
	handleSelected?: () => void;
	isSelected?: boolean;
}

const AudioPlayer: React.FC<VoiceAudioPlayerProps> = ({
	fileName,
	onPlay,
	onPause,
	currentlyPlayingIndex,
	index,
	handleSelected,
	isSelected,
}) => {
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isSelecting, setIsSelecting] = useState<boolean>(false);

	const debouncedSelection = useCallback(
		debounce(() => {
			handleSelected?.();
			setIsSelecting(false);
		}, 1000),
		[handleSelected],
	);

	const onSelectClick = () => {
		if (!isSelected) {
			setIsSelecting(true);
			debouncedSelection();
		}
	};

	useEffect(() => {
		if (audioRef.current) {
			if (currentlyPlayingIndex !== index && !audioRef.current.paused) {
				try {
					audioRef.current.pause();
					setIsPlaying(false);
					onPause?.();
				} catch (error) {
					console.error('Pause error:', error);
				}
			}
		}
	}, [currentlyPlayingIndex, index, onPause]);

	const presignedUrlMutation = useMutation({
		mutationFn: generateSignedUrl,
		onSuccess: ({ url }) => {
			if (audioRef.current && url) {
				audioRef.current.src = url;
				audioRef.current.play();
				setIsPlaying(true);
				onPlay?.();
			}
		},
		onError: (error) => {
			console.error('Failed to fetch presigned URL:', error);
		},
	});

	const togglePlayPause = () => {
		if (audioRef.current) {
			if (audioRef.current.paused) {
				if (!audioRef.current.src) {
					presignedUrlMutation.mutate({
						objectKey: `voices/${fileName}`,
						bucketType: 'voices',
					});
				} else {
					audioRef.current
						.play()
						.then(() => {
							setIsPlaying(true);
							onPlay?.();
						})
						.catch((error) => {
							console.error('Play error:', error);
							notification.error({
								message: 'Audio Playback Error',
								description: 'Unable to play the audio file. Please try again.',
								placement: 'topRight',
								className: 'compact-notification',
							});
							setIsPlaying(false);
							onPause?.();
						});
				}
			} else {
				try {
					audioRef.current.pause();
				} catch (error) {
					console.error('Pause error:', error);
					notification.error({
						message: 'Pause Error',
						description: 'Unable to pause the audio.',
						placement: 'topRight',
						className: 'compact-notification',
					});
				}
				setIsPlaying(false);
				onPause?.();
			}
		}
	};

	const isDisabled = currentlyPlayingIndex !== null && currentlyPlayingIndex !== index;

	return (
		<div className="flex items-center justify-between w-full gap-2 px-2">
			<audio
				ref={audioRef}
				preload="none"
				onEnded={() => {
					setIsPlaying(false);
					onPause?.();
				}}
			/>

			{/* Select button */}
			<button
				onClick={onSelectClick}
				disabled={isSelecting || isSelected}
				className={`
          flex items-center justify-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2
          rounded-md text-xs sm:text-sm font-medium flex-shrink-0
          transition-all duration-200 border
          ${
						isSelected
							? 'bg-gray-900 border-gray-900 text-white cursor-default'
							: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300'
					}
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
			>
				{isSelected ? <CheckCircle size={16} /> : <PlusCircle size={16} />}
				<span className="whitespace-nowrap">
					{isSelected ? 'Selected' : isSelecting ? 'Selecting...' : 'Select'}
				</span>
			</button>

			{/* Play/Pause button */}
			<button
				onClick={togglePlayPause}
				disabled={presignedUrlMutation.isPending || isDisabled}
				className={`
          flex items-center justify-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2
          rounded-md text-xs sm:text-sm font-medium flex-shrink-0
          transition-all duration-200 border
          disabled:opacity-50 disabled:cursor-not-allowed
          ${
						isPlaying
							? 'bg-gray-900 border-gray-900 text-white hover:bg-gray-800'
							: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300'
					}
        `}
			>
				{isPlaying ? <Pause size={16} /> : <Play size={16} />}
				<span className="whitespace-nowrap">{isPlaying ? 'Playing' : 'Preview'}</span>
			</button>
		</div>
	);
};

export default AudioPlayer;
